import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import Modal from "@/components/ui/UIElements/Modal";
import { Icon } from "@/components/ui";
import { getWhatsAppTestPayload } from "@/lib/whatsappTestLink";

const WhatsAppTestModal = ({ isOpen, onClose, agent }) => {
  const { t } = useTranslation("aiAgents");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);

  const { waUrl, isReady, countryCodeDigits, phoneDigits } = useMemo(
    () => getWhatsAppTestPayload(agent),
    [agent],
  );

  useEffect(() => {
    let isCancelled = false;

    const generateQrCode = async () => {
      if (!isOpen || !waUrl) {
        setQrCodeDataUrl("");
        setIsGeneratingQr(false);
        return;
      }

      setIsGeneratingQr(true);
      try {
        const dataUrl = await QRCode.toDataURL(waUrl, {
          width: 256,
          margin: 1,
        });
        if (!isCancelled) {
          setQrCodeDataUrl(dataUrl);
        }
      } catch {
        if (!isCancelled) {
          setQrCodeDataUrl("");
        }
      } finally {
        if (!isCancelled) {
          setIsGeneratingQr(false);
        }
      }
    };

    generateQrCode();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, waUrl]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("whatsappTestModal.title")}
      subtitle={t("whatsappTestModal.description")}
      maxWidth="max-w-md"
    >
      {!isReady ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-yellow-800 bg-yellow-900/20 p-4">
            <div className="flex items-start gap-3">
              <Icon
                name="AlertTriangle"
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500"
              />
              <p className="text-sm text-yellow-300">
                {t("card.actions.testWhatsAppMissingData")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-gray-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700"
          >
            {t("actions.cancel")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
            <p className="text-sm text-gray-300">
              <span className="font-medium text-white">
                {t("whatsappTestModal.phoneLabel")}:
              </span>{" "}
              +{countryCodeDigits} {phoneDigits}
            </p>
          </div>

          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-500"
          >
            <Icon name="ExternalLink" className="mr-2 h-4 w-4" />
            {t("whatsappTestModal.openLink")}
          </a>

          <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
            <p className="mb-3 text-sm text-gray-300">
              {t("whatsappTestModal.qrHint")}
            </p>
            <div className="flex items-center justify-center rounded-md bg-white p-3">
              {qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt={t("whatsappTestModal.qrAlt")}
                  className="h-56 w-56"
                />
              ) : (
                <div className="flex h-56 w-56 items-center justify-center">
                  {isGeneratingQr ? (
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                  ) : (
                    <span className="text-sm text-gray-500">
                      {t("card.actions.testWhatsAppMissingData")}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default WhatsAppTestModal;
